import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Container, PageTitle, NbCard, NbButton, Chip } from "@/components/nb";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";

const FOCUS = ["all", "tech", "design", "science", "business", "arts", "impact"];

export default function Mentors() {
  const [mentors, setMentors] = useState([]);
  const [focus, setFocus] = useState("all");
  const [q, setQ] = useState("");
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    const url = focus === "all" ? "/career/mentors" : `/career/mentors?focus=${focus}`;
    api.get(url).then((r) => setMentors(r.data));
  }, [focus]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return mentors;
    return mentors.filter((m) => (m.name || "").toLowerCase().includes(query) || (m.role || "").toLowerCase().includes(query) || (m.bio || "").toLowerCase().includes(query) || (m.focus || "").toLowerCase().includes(query));
  }, [mentors, q]);

  const request = async (id) => {
    if (!user) { toast.error("Please sign in first"); return nav("/auth"); }
    await api.post(`/career/mentors/${id}/request`);
    toast.success("Request sent! Mentor will reach out via email.");
  };

  return (
    <Container>
      <PageTitle eyebrow="MENTOR CONNECT" title="Talk to someone who's done it." />
      <div className="flex items-center gap-3 bg-white nb-border nb-shadow rounded-xl px-4 py-3 max-w-2xl mb-4">
        <Search className="w-5 h-5 shrink-0" />
        <input data-testid="mentor-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search mentors by name, role or focus..." className="flex-1 bg-transparent outline-none py-1" />
        {q && <button onClick={() => setQ("")} data-testid="clear-mentor" className="font-mono-sl text-[10px] uppercase underline">Clear</button>}
      </div>
      <div className="flex flex-wrap gap-2 mb-6" data-testid="mentor-filter">
        {FOCUS.map((f) => (
          <button key={f} onClick={() => setFocus(f)} data-testid={`filter-${f}`} className={`px-3 py-1 rounded-full nb-border font-mono-sl text-[11px] uppercase ${focus === f ? "bg-sl-accent nb-shadow" : "bg-white"}`}>{f}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="mentor-list">
        {filtered.length === 0 && <div className="text-neutral-500">No mentors match your search.</div>}
        {filtered.map((m) => (
          <NbCard key={m.id} className="!p-5 flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-sl-primary nb-border grid place-items-center font-heading font-black text-xl">{m.avatar}</div>
              <div>
                <div className="font-heading font-black text-lg">{m.name}</div>
                <div className="text-sm text-neutral-700">{m.role}</div>
              </div>
            </div>
            <Chip color="tertiary">{m.focus.toUpperCase()}</Chip>
            <p className="text-sm text-neutral-800">{m.bio}</p>
            <NbButton onClick={() => request(m.id)} color="primary" data-testid={`req-${m.id}`}>Request intro</NbButton>
          </NbCard>
        ))}
      </div>
    </Container>
  );
}
